import React, { useEffect, useMemo, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./LandingPage.scss";
import { useTranslation } from "react-i18next";
import { sendEmail } from "@utils/api-communication/RestApiCli";
import { isEmailValid } from "@utils/CommonUtil";
import { isOfflineFrontendMode } from "@utils/deviceSetupNavigation";
import {
  IconActivity,
  IconAdjustments,
  IconArrowRight,
  IconBrain,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBriefcase,
  IconChartBar,
  IconChartLine,
  IconCircleCheck,
  IconCloud,
  IconDownload,
  IconEye,
  IconEyeglass,
  IconFileAnalytics,
  IconFileText,
  IconHeadset,
  IconMail,
  IconMapPin,
  IconMicrophone,
  IconPhone,
  IconPlayerPlayFilled,
  IconSparkles,
  IconStethoscope,
  IconSun,
  IconTrendingUp,
  IconWifi,
} from "@tabler/icons-react";

const STUDY_ICONS = [IconChartLine, IconActivity, IconTrendingUp, IconBrain];

const REGULATORY_KEYS = ["fda", "mhra", "iso"];

const OVERVIEW_CERT_KEYS: {
  src: string;
  altKey: string;
  captionKey: string;
  note?: string;
}[] = [
  {
    src: "/img/certs/2.png",
    altKey: "overview.certs.ce",
    captionKey: "overview.certCaptions.ce",
    note: "1984",
  },
];

const COUNTRY_KEYS = [
  "uk",
  "us",
  "de",
  "fr",
  "nl",
  "es",
  "it",
  "tr",
  "ae",
  "sa",
  "au",
  "ca",
  "in",
  "sg",
  "other",
];

const VF_CORE_KEYS = ["threshold", "suprathreshold", "esterman", "ptosis", "foveal"];
const VF_ADV_KEYS = ["kinetic", "swap", "fdt"];
const BEYOND_KEYS = [
  "va",
  "color",
  "contrast",
  "amsler",
  "motility",
  "pupillometry",
  "coverTest",
  "phoria",
  "stereo",
];

const CITY_KEYS = ["boston", "dubai", "ankara"];

export default function LandingSections() {
  const { t } = useTranslation("landing");
  const { t: tTranslation } = useTranslation();
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    clinic: "",
    country: "",
    message: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 60 });
  }, []);

  function handleFormChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isEmailValid(formState.email)) {
      return;
    }
    const countryLabel = formState.country ? t(`contact.countries.${formState.country}`) : "";
    const message = [`Clinic / Hospital: ${formState.clinic}`, "", formState.message].join("\n");
    sendEmail({
      name: formState.name,
      email: formState.email,
      country: countryLabel,
      message,
    });
    setFormSubmitted(true);
  }

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const overviewFeatures = useMemo(
    () => [
      { icon: <IconBriefcase size={20} />, key: "portable" },
      { icon: <IconSun size={20} />, key: "noDarkRoom" },
      { icon: <IconEye size={20} />, key: "noEyePatch" },
      { icon: <IconCloud size={20} />, key: "instantResults" },
      { icon: <IconMicrophone size={20} />, key: "virtualAssistant" },
      { icon: <IconDownload size={20} />, key: "dicomPdf" },
      { icon: <IconWifi size={20} />, key: "onlineOffline" },
    ],
    [],
  );

  const steps = useMemo(
    () => [
      { num: "1", key: "setup", icon: <IconHeadset size={32} /> },
      { num: "2", key: "test", icon: <IconAdjustments size={32} /> },
      { num: "3", key: "monitor", icon: <IconEye size={32} /> },
      { num: "4", key: "review", icon: <IconFileAnalytics size={32} /> },
    ],
    [],
  );

  const resultFeatures = useMemo(
    () => [
      { icon: <IconAdjustments size={15} />, key: "sensitivity" },
      { icon: <IconBrain size={15} />, key: "ght" },
      { icon: <IconChartBar size={15} />, key: "vfi" },
      { icon: <IconActivity size={15} />, key: "mdpsd" },
      { icon: <IconEye size={15} />, key: "gaze" },
      { icon: <IconTrendingUp size={15} />, key: "progression" },
      { icon: <IconFileText size={15} />, key: "dicom" },
    ],
    [],
  );

  return (
    <>
      {/* ─── PRODUCT OVERVIEW ─── */}
      <section className="lp-section lp-overview" id="product">
        <div className="lp-container">
          <div className="lp-overview-header" data-aos="fade-up">
            <h2 className="lp-section-title">{t("overview.title")}</h2>
            <p className="lp-section-subtitle">{t("overview.subtitle")}</p>
          </div>

          <div className="lp-overview-body">
            <div className="lp-overview-images" data-aos="fade-right">
              <div className="lp-img-circle lp-img-circle-1">
                <img
                  src="/img/studio/headset_hang.png"
                  alt={t("overview.deviceAlt")}
                  width="217"
                  height="193"
                  loading="lazy"
                />
              </div>
              <div className="lp-img-circle lp-img-circle-2">
                <img
                  src="/img/studio/headset_back.png"
                  alt={t("overview.inUseAlt")}
                  width="400"
                  height="313"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lp-overview-features" data-aos="fade-left">
              {overviewFeatures.map((f, i) => (
                <div className="lp-ovf-item" key={f.key} data-aos="fade-up" data-aos-delay={i * 60}>
                  <div className="ovf-icon">{f.icon}</div>
                  <div>
                    <h4>{t(`overview.features.${f.key}.title`)}</h4>
                    <p>{t(`overview.features.${f.key}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lp-overview-certs" data-aos="fade-up">
            <p className="lp-overview-cert-claim">{t("overview.certClaim")}</p>
            <div className="lp-overview-cert-columns">
              <div className="lp-overview-cert-row">
                {OVERVIEW_CERT_KEYS.map((c) => {
                  const alt = t(c.altKey);
                  return (
                    <div key={c.altKey} className="lp-overview-cert-item">
                      <span className="lp-overview-cert-logo">
                        <img
                          className="lp-overview-cert"
                          src={c.src}
                          alt={alt}
                          width="500"
                          height="500"
                          loading="lazy"
                        />
                      </span>
                      <span className="lp-overview-cert-note">{c.note ?? " "}</span>
                      <span className="lp-overview-cert-caption">{t(c.captionKey)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="lp-overview-cert-statement">
                {REGULATORY_KEYS.map((k) => (
                  <span key={k}>{t(`overview.regulatory.${k}`)}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="lp-section lp-how lp-section--alt" id="how-it-works">
        <div className="lp-container">
          <div className="lp-how-header" data-aos="fade-up">
            <h2 className="lp-section-title">{t("how.title")}</h2>
            <p className="lp-section-subtitle" style={{ margin: "0 auto 64px" }}>
              {t("how.subtitle")}
            </p>
          </div>

          {!isOfflineFrontendMode() && (
            <div className="lp-how-video" data-aos="fade-up">
              {videoPlaying ? (
                <iframe
                  src="https://www.youtube.com/embed/QKlcFtGAGOQ?autoplay=1&rel=0"
                  title={t("how.videoTitle")}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  className="lp-how-video-thumb"
                  onClick={() => setVideoPlaying(true)}
                  aria-label={t("how.videoPlayLabel")}
                >
                  <img
                    src="/img/studio/thumbnail.jpg"
                    alt={t("how.videoAlt")}
                    width="1280"
                    height="720"
                    loading="lazy"
                  />
                  <span className="lp-how-video-play">
                    <IconPlayerPlayFilled size={34} />
                  </span>
                </button>
              )}
            </div>
          )}

          <div className="lp-steps">
            {steps.map((step, i) => (
              <div className="lp-step" key={step.num} data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="step-number-wrap">{step.num}</div>
                <h3>{t(`how.steps.${step.key}.title`)}</h3>
                <p>{t(`how.steps.${step.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VISUAL FIELD AND BEYOND ─── */}
      <section className="lp-section lp-vfab" id="tests">
        <div className="lp-container">
          <div className="lp-vfab-header" data-aos="fade-up">
            <h2 className="lp-section-title">{t("vfab.title")}</h2>
            <p className="lp-section-subtitle" style={{ margin: "0 auto 52px" }}>
              {t("vfab.subtitle")}
            </p>
          </div>

          <div className="lp-vfab-grid">
            {/* Left card: Visual Field */}
            <div className="lp-vfab-card" data-aos="fade-right">
              <div className="vfab-card-header vfab-card-header--vf">
                <span className="vfab-card-icon">
                  <IconEyeglass size={22} />
                </span>
                <h3>{t("vfab.visualField")}</h3>
              </div>
              <div className="vfab-card-body">
                {VF_CORE_KEYS.map((k) => (
                  <div className="vfab-test-item" key={k}>
                    <span className="vfab-dot" />
                    {t(`vfab.vfCore.${k}`)}
                  </div>
                ))}
                <div className="vfab-divider">
                  <span>{t("vfab.advanced")}</span>
                </div>
                {VF_ADV_KEYS.map((k) => (
                  <div className="vfab-test-item vfab-test-item--adv" key={k}>
                    <span className="vfab-dot vfab-dot--adv" />
                    {t(`vfab.vfAdv.${k}`)}
                  </div>
                ))}
              </div>
            </div>

            {/* Right card: Beyond */}
            <div className="lp-vfab-card" data-aos="fade-left">
              <div className="vfab-card-header vfab-card-header--beyond">
                <span className="vfab-card-icon">
                  <IconStethoscope size={22} />
                </span>
                <h3>{t("vfab.beyond")}</h3>
              </div>
              <div className="vfab-card-body">
                {BEYOND_KEYS.map((k) => (
                  <div className="vfab-test-item" key={k}>
                    <span className="vfab-dot" />
                    {t(`vfab.beyondTests.${k}`)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lp-vfab-highlight" data-aos="fade-up">
            <span className="lp-vfab-highlight-icon">
              <IconSparkles size={20} />
            </span>
            <p>
              {t("vfab.highlight")} <span>{t("vfab.highlightSub")}</span>
            </p>
          </div>
        </div>
      </section>

      {/* ─── RESULTS & ANALYTICS ─── */}
      <section className="lp-section lp-results lp-section--alt" id="results">
        <div className="lp-container">
          <div className="lp-results-header" data-aos="fade-up">
            <h2 className="lp-section-title">{t("results.title")}</h2>
            <p className="lp-section-subtitle">{t("results.subtitle")}</p>
          </div>

          <div className="lp-rs-grid">
            {/* Top-left: dB Map */}
            <div className="lp-rs-panel lp-rs-panel-left lp-rs-row1" data-aos="fade-right">
              <img
                src="/img/test_results/result3.png"
                alt={t("results.labels.dbMap")}
                width="720"
                height="674"
                loading="lazy"
              />
              <div className="lp-rs-label">
                <span className="lp-rs-dot" />
                {t("results.labels.dbMap")}
              </div>
            </div>

            {/* Bottom-left: Eye Tracking */}
            <div
              className="lp-rs-panel lp-rs-panel-left lp-rs-row2"
              data-aos="fade-right"
              data-aos-delay="120"
            >
              <img
                src="/img/test_results/result2.png"
                alt={t("results.labels.eyeTracking")}
                width="576"
                height="268"
                loading="lazy"
              />
              <div className="lp-rs-label">
                <span className="lp-rs-dot" />
                {t("results.labels.eyeTracking")}
              </div>
            </div>

            {/* Centre: tablet, spans both rows */}
            <div className="lp-rs-tablet-wrap" data-aos="zoom-in">
              <img
                src="/img/main_page_header/new/5.png"
                alt={t("results.tabletAlt")}
                className="lp-rs-tablet"
                width="1414"
                height="2000"
                loading="lazy"
              />
            </div>

            {/* Top-right: Grayscale */}
            <div className="lp-rs-panel lp-rs-panel-right lp-rs-row1" data-aos="fade-left">
              <img
                src="/img/test_results/result1.png"
                alt={t("results.labels.grayscale")}
                width="874"
                height="732"
                loading="lazy"
              />
              <div className="lp-rs-label lp-rs-label-right">
                {t("results.labels.grayscale")}
                <span className="lp-rs-dot" />
              </div>
            </div>

            {/* Bottom-right: Deviation */}
            <div
              className="lp-rs-panel lp-rs-panel-right lp-rs-row2"
              data-aos="fade-left"
              data-aos-delay="120"
            >
              <img
                src="/img/test_results/result0.png"
                alt={t("results.labels.deviation")}
                width="336"
                height="284"
                loading="lazy"
              />
              <div className="lp-rs-label lp-rs-label-right">
                {t("results.labels.deviation")}
                <span className="lp-rs-dot" />
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="lp-results-stats" data-aos="fade-up">
            {[
              { key: "GHT", value: t("results.stats.ghtValue"), highlight: false },
              { key: "VFI", value: "54.76%", highlight: true },
              { key: "MD", value: "−15.15 dB", highlight: true },
              { key: "PSD", value: "11.15 dB", highlight: false },
            ].map((s) => (
              <div className="lp-stat-chip" key={s.key}>
                <span className="lp-stat-key">{s.key}</span>
                <span className={`lp-stat-val${s.highlight ? " lp-stat-val--hl" : ""}`}>
                  {s.value}
                </span>
              </div>
            ))}
          </div>

          {/* Feature list */}
          <div className="lp-rs-feats" data-aos="fade-up" data-aos-delay="80">
            {resultFeatures.map((f) => (
              <div className="lp-rs-feat-item" key={f.key}>
                <span className="lp-rs-feat-icon">{f.icon}</span>
                {t(`results.features.${f.key}`)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── STUDIES ─── */}
      <section className="lp-section lp-studies" id="studies">
        <div className="lp-container">
          <div className="lp-studies-header" data-aos="fade-up">
            <h2 className="lp-section-title">{tTranslation("Studies.name")}</h2>
          </div>

          <div className="lp-studies-grid">
            {(() => {
              const studies = tTranslation("Studies.list", { returnObjects: true });
              const studyArray = Array.isArray(studies)
                ? studies
                : studies
                  ? Object.values(studies)
                  : [];
              return studyArray.map((d: any, i: number) => {
                const StudyIcon = STUDY_ICONS[i % STUDY_ICONS.length];
                return (
                  <div
                    className="lp-study-card"
                    key={i}
                    data-aos="fade-up"
                    data-aos-delay={i * 100}
                  >
                    <div className="lp-study-icon">
                      <StudyIcon size={18} />
                    </div>
                    <a href={d.link} target="_blank" rel="noopener noreferrer">
                      <h3>{d.title}</h3>
                    </a>
                    <p>{d.text}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ─── CONTACT / DEMO ─── */}
      <section className="lp-section lp-contact" id="contact">
        <div className="lp-container">
          <div className="lp-contact-inner">
            <div className="lp-contact-info" data-aos="fade-right">
              <h2 className="lp-section-title">{t("contact.title")}</h2>
              <p style={{ color: "var(--lp-text-muted)" }}>{t("contact.subtitle")}</p>

              <div className="contact-items">
                {[
                  {
                    icon: <IconMail size={20} />,
                    label: t("contact.general"),
                    content: <a href="mailto:info@oculera.health">info@oculera.health</a>,
                  },
                  {
                    icon: <IconPhone size={20} />,
                    label: t("contact.sales"),
                    content: <a href="mailto:dist@oculera.health">dist@oculera.health</a>,
                  },
                ].map((item) => (
                  <div className="contact-item" key={item.label}>
                    <div className="ci-icon">{item.icon}</div>
                    <div className="ci-text">
                      <label>{item.label}</label>
                      {item.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="lp-contact-offices">
                <h4 className="lp-contact-offices-title">{t("contact.offices")}</h4>
                <div className="lp-contact-office-list">
                  {CITY_KEYS.map((city) => (
                    <div className="lp-contact-office" key={city}>
                      <IconMapPin size={18} />
                      <span>{t(`contact.cities.${city}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lp-contact-form" data-aos="fade-left">
              {formSubmitted ? (
                <div className="form-success">
                  <div className="success-icon">
                    <IconCircleCheck size={32} color="#16a34a" />
                  </div>
                  <h3>{t("contact.form.successTitle")}</h3>
                  <p>{t("contact.form.successDesc")}</p>
                </div>
              ) : (
                <>
                  <h3>{t("contact.form.title")}</h3>
                  <form onSubmit={handleFormSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">{t("contact.form.fullName")}</label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          placeholder={t("contact.form.namePlaceholder")}
                          value={formState.name}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">{t("contact.form.email")}</label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          placeholder={t("contact.form.emailPlaceholder")}
                          value={formState.email}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="clinic">{t("contact.form.clinic")}</label>
                        <input
                          id="clinic"
                          name="clinic"
                          type="text"
                          placeholder={t("contact.form.clinicPlaceholder")}
                          value={formState.clinic}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="country">{t("contact.form.country")}</label>
                        <select
                          id="country"
                          name="country"
                          value={formState.country}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">{t("contact.form.selectCountry")}</option>
                          {COUNTRY_KEYS.map((c) => (
                            <option key={c} value={c}>
                              {t(`contact.countries.${c}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">{t("contact.form.message")}</label>
                      <textarea
                        id="message"
                        name="message"
                        placeholder={t("contact.form.messagePlaceholder")}
                        value={formState.message}
                        onChange={handleFormChange}
                      />
                    </div>

                    <button type="submit" className="lp-btn-primary form-submit">
                      {t("contact.form.submit")} <IconArrowRight size={18} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo">
                <img
                  src="/img/logo.png"
                  alt="Oculera"
                  width="455"
                  height="150"
                  loading="lazy"
                  style={{ height: 30, width: "auto" }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </a>
              <p>{t("footer.brandTagline")}</p>
              <div className="footer-socials">
                <a
                  href="https://www.instagram.com/oculerahealth/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("footer.social.instagram")}
                >
                  <IconBrandInstagram size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/company/74855414/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("footer.social.linkedin")}
                >
                  <IconBrandLinkedin size={18} />
                </a>
                <a href="mailto:info@oculera.health" aria-label={t("footer.social.email")}>
                  <IconMail size={18} />
                </a>
              </div>
            </div>

            <div className="footer-col">
              <h4>{t("footer.product")}</h4>
              <ul>
                <li>
                  <a
                    href="#product"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo("product");
                    }}
                  >
                    {t("footer.overview")}
                  </a>
                </li>
                <li>
                  <a
                    href="#tests"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo("tests");
                    }}
                  >
                    {t("footer.testLibrary")}
                  </a>
                </li>
                <li>
                  <a
                    href="#results"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo("results");
                    }}
                  >
                    {t("footer.resultsLink")}
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t("footer.company")}</h4>
              <ul>
                <li>
                  <a href="/blog">{t("footer.blog")}</a>
                </li>
                <li>
                  <a href="/careers">{t("footer.careers")}</a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t("footer.contactCol")}</h4>
              <ul>
                <li>
                  <a href="mailto:info@oculera.health">info@oculera.health</a>
                </li>
                <li>
                  <a href="mailto:dist@oculera.health">dist@oculera.health</a>
                </li>
                <li>
                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo("contact");
                    }}
                  >
                    {t("footer.contactUs")}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="lp-footer-bottom">
            <p className="copyright">{t("footer.copyright", { year: new Date().getFullYear() })}</p>

            <div className="footer-reg-block">
              <div className="footer-reg-badges">
                {OVERVIEW_CERT_KEYS.map((c) => (
                  <span className="footer-cert-item" key={c.altKey}>
                    <img
                      className="footer-cert-logo"
                      src={c.src}
                      alt={t(c.altKey)}
                      width="500"
                      height="500"
                      loading="lazy"
                    />
                    {c.note && <span className="footer-cert-note">{c.note}</span>}
                  </span>
                ))}
              </div>
              <div className="footer-reg-statement">
                {REGULATORY_KEYS.map((k) => (
                  <span key={k}>{t(`overview.regulatory.${k}`)}</span>
                ))}
              </div>
            </div>

            <div className="footer-legal">
              <a href="/privacy">{t("footer.privacy")}</a>
              <a href="/terms">{t("footer.terms")}</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
